import { useState } from 'react';
import { css } from '@emotion/react';
import { motion } from 'motion/react';
import { postFeedback } from '@frontend/api-insurance-eligibility-report';
import { getUser } from '@frontend/auth-helpers';
import { http } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  TextButton,
  PrimaryButton,
  Text,
  StarIcon,
  TextareaField,
  useFormField,
  CheckIcon,
  useAlert,
} from '@frontend/design-system';
import { alignHorizontal, alignItemsCenter } from './common-styles';

enum FeedbackState {
  STARS = 'start',
  FEEDBACK = 'feedback',
  THANKYOU = 'thankYou',
}

const Rating = [1, 2, 3, 4, 5];
const INITIAL_FEEDBACK_STATE = { rating: '', feedback: '' };

export const Feedback = () => {
  const [feedbackState, setFeedbackState] = useState<FeedbackState>(FeedbackState.STARS);
  const [feedback, setFeedback] = useState<{ rating: string; feedback: string }>(INITIAL_FEEDBACK_STATE);

  const { t } = useTranslation('insuranceEligibilityReport');
  const textField = useFormField({ type: 'text', placeholder: t('Your feedback (Optional)') });
  const alert = useAlert();

  const onClickItem = (rating: string | number) => {
    setFeedback((state) => ({ ...state, rating: rating.toString() }));
    setFeedbackState(FeedbackState.FEEDBACK);
  };

  const onSubmit = () => {
    const newState = { ...feedback, feedback: textField.value ?? '' };
    setFeedback(newState);
    setFeedbackState(FeedbackState.THANKYOU);
    postFeedback({
      feedback_id: 'insurance-eligibility-report-revamped',
      feedback_map: {
        route: window?.location?.pathname,
        domain: http.baseUrl,
        ...newState,
      },
      user_id: getUser()?.userID ?? '',
    }).catch(() => {
      setFeedbackState(FeedbackState.STARS);
      setFeedback(INITIAL_FEEDBACK_STATE);
      alert.error(t('Unable to post your feedback'));
    });
  };

  return (
    <div css={container}>
      <motion.div
        css={fitContentWidth}
        animate={{
          opacity: feedbackState === FeedbackState.STARS ? 1 : 0,
          position: feedbackState === FeedbackState.STARS ? 'relative' : 'absolute',
        }}
        transition={{ duration: 0.5 }}
        layout
      >
        <Text weight='bold' size='large' css={{ marginBottom: theme.spacing(2) }}>
          {t('Rate your experience with our product...')}
        </Text>
        <span>
          {Rating.map((item, index: number) => (
            <TextButton css={ratingButtonStyle} onClick={() => onClickItem(item)} key={`feedback-${item}-${index}`}>
              {item}
            </TextButton>
          ))}

          <StarIcon css={{ margin: theme.spacing(0, 0.5, 0, 1) }} size={16} />
          <Text size='large'>{t('Stars')}</Text>
        </span>
      </motion.div>
      <motion.div
        css={fitContentWidth}
        animate={{
          opacity: feedbackState === FeedbackState.FEEDBACK ? 1 : 0,
          display: feedbackState === FeedbackState.FEEDBACK ? 'block' : 'none',
          position: feedbackState === FeedbackState.FEEDBACK ? 'relative' : 'absolute',
        }}
        transition={{ duration: 0.5 }}
        layout
      >
        <span css={spaceBetween}>
          <Text size='large' weight='bold'>
            {t('Anything that can be improved?')}
          </Text>
          <span>
            <StarIcon size={16} css={{ marginRight: theme.spacing(0.5) }} />
            <Text size='large'>{feedback.rating}</Text>
          </span>
        </span>
        <TextareaField label='' name='' {...textField} css={inputArea} />
        <PrimaryButton css={fitContentWidth} onClick={onSubmit}>
          {t('Submit')}
        </PrimaryButton>
      </motion.div>
      <motion.div
        css={[alignHorizontal, alignItemsCenter, fitContentWidth]}
        animate={{
          opacity: feedbackState === FeedbackState.THANKYOU ? 1 : 0,
          position: feedbackState === FeedbackState.THANKYOU ? 'relative' : 'absolute',
        }}
        transition={{ duration: 0.5 }}
        layout
      >
        <CheckIcon css={greenBackgroundTick} />
        <Text size='large'>{t('Thanks for the feedback!')}</Text>
      </motion.div>
    </div>
  );
};

const container = css`
  background-color: ${theme.colors.white};
  padding: ${theme.spacing(2, 3)};
  border-radius: ${theme.borderRadius.small};
  width: fit-content;
  margin: ${theme.spacing(3)} auto 0;
  overflow: hidden;

  span {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`;

const spaceBetween = css`
  justify-content: space-between;
`;

const fitContentWidth = css`
  width: fit-content;
`;

const ratingButtonStyle = css`
  margin: ${theme.spacing(0, 0.25)};
  border-radius: ${theme.borderRadius.small};
  border: 1px solid ${theme.colors.neutral30};
  height: ${theme.spacing(4)};
  width: ${theme.spacing(4)};
`;

const inputArea = css`
  height: ${theme.spacing(15)};
  width: ${theme.spacing(42)};
  margin: ${theme.spacing(2, 0)};
`;

const greenBackgroundTick = css`
  background-color: ${theme.colors.success50};
  border-radius: 50%;
  fill: ${theme.colors.white};
  padding: ${theme.spacing(0.5)};
  margin: ${theme.spacing(0, 0.5, 0, 0)};
`;
